import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { NavItem, UserRole, ROLE_NAV_CONFIG } from "@/lib/roles";
import { useToast } from "@/hooks/use-toast";

export interface NavPreferences {
  favorites: NavItem[];
  expandedGroups: string[];
  isCompact: boolean;
  isCustomized: boolean;
  updatedAt: string | null;
}

export function useNavPreferences() {
  const { user, role } = useAuth();
  const { activeCompany } = useCompany();
  const { toast } = useToast();

  const [favorites, setFavorites] = useState<NavItem[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(["OVERVIEW", "ACCOUNTING"]);
  const [isCompact, setIsCompact] = useState<boolean>(false);
  const [isCustomized, setIsCustomized] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadedRef = useRef<boolean>(false);

  // Helper to extract auth headers
  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    try {
      const sess = localStorage.getItem("accoupro_auth_session");
      if (sess) {
        const parsed = JSON.parse(sess);
        if (parsed.access_token) headers["Authorization"] = `Bearer ${parsed.access_token}`;
      }
      if (user?.id) headers["x-user-id"] = user.id;
      if (activeCompany?.id) headers["x-company-id"] = activeCompany.id;
    } catch {}
    return headers;
  }, [user?.id, activeCompany?.id]);

  // Load preferences from backend
  const loadPreferences = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const companyId = activeCompany?.id || "system";

    try {
      const res = await fetch(`/api/nav/preferences?companyId=${companyId}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success && data.preferences) {
        setFavorites(data.preferences.favorites || []);
        setExpandedGroups(data.preferences.expandedGroups || ["OVERVIEW", "ACCOUNTING"]);
        setIsCompact(Boolean(data.preferences.isCompact));
        setIsCustomized(Boolean(data.isCustomized));
      }
    } catch (err) {
      console.warn("[SmartSidebar] Failed to load preferences from server, using role defaults:", err);
      // Fallback
      const groups = ROLE_NAV_CONFIG[role as UserRole] || [];
      const fallbackFavs: NavItem[] = [];
      groups.slice(0, 2).forEach((g) => {
        g.items.slice(0, 3).forEach((item) => fallbackFavs.push(item));
      });
      setFavorites(fallbackFavs);
    } finally {
      setLoading(false);
      initialLoadedRef.current = true;
    }
  }, [user, activeCompany?.id, role, getAuthHeaders]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Save preferences to backend with debouncing
  const persistPreferences = useCallback(
    (newFavorites: NavItem[], newExpanded: string[], compactState: boolean) => {
      if (!initialLoadedRef.current || !user) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const companyId = activeCompany?.id || "system";
          await fetch("/api/nav/preferences", {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              companyId,
              favorites: newFavorites,
              expandedGroups: newExpanded,
              isCompact: compactState,
            }),
          });
          setIsCustomized(true);
        } catch (err) {
          console.error("[SmartSidebar] Failed to persist nav preferences:", err);
        }
      }, 500);
    },
    [user, activeCompany?.id, getAuthHeaders]
  );

  // Toggle favorite
  const toggleFavorite = useCallback(
    (item: NavItem) => {
      setFavorites((prev) => {
        const exists = prev.some((f) => f.url === item.url);
        let updated: NavItem[];
        if (exists) {
          updated = prev.filter((f) => f.url !== item.url);
          toast({ title: "Removed from Favorites", description: `${item.title} removed from pinned shortcuts.` });
        } else {
          updated = [...prev, item];
          toast({ title: "Pinned to Favorites", description: `${item.title} added to quick access.` });
        }
        persistPreferences(updated, expandedGroups, isCompact);
        return updated;
      });
    },
    [expandedGroups, isCompact, persistPreferences, toast]
  );

  // Remove single favorite
  const removeFavorite = useCallback(
    (url: string) => {
      setFavorites((prev) => {
        const target = prev.find((f) => f.url === url);
        const updated = prev.filter((f) => f.url !== url);
        if (target) {
          toast({ title: "Removed from Favorites", description: `${target.title} unpinned.` });
        }
        persistPreferences(updated, expandedGroups, isCompact);
        return updated;
      });
    },
    [expandedGroups, isCompact, persistPreferences, toast]
  );

  // Reorder favorites
  const reorderFavorites = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      setFavorites((prev) => {
        if (
          sourceIndex < 0 ||
          sourceIndex >= prev.length ||
          destinationIndex < 0 ||
          destinationIndex >= prev.length
        ) {
          return prev;
        }
        const updated = [...prev];
        const [moved] = updated.splice(sourceIndex, 1);
        updated.splice(destinationIndex, 0, moved);
        persistPreferences(updated, expandedGroups, isCompact);
        return updated;
      });
    },
    [expandedGroups, isCompact, persistPreferences]
  );

  // Toggle group expansion
  const toggleGroup = useCallback(
    (groupId: string) => {
      setExpandedGroups((prev) => {
        const isOpen = prev.includes(groupId);
        const updated = isOpen ? prev.filter((id) => id !== groupId) : [...prev, groupId];
        persistPreferences(favorites, updated, isCompact);
        return updated;
      });
    },
    [favorites, isCompact, persistPreferences]
  );

  // Ensure a specific group is expanded (for active route auto-expand)
  const expandGroup = useCallback(
    (groupId: string) => {
      setExpandedGroups((prev) => {
        if (prev.includes(groupId)) return prev;
        const updated = [...prev, groupId];
        persistPreferences(favorites, updated, isCompact);
        return updated;
      });
    },
    [favorites, isCompact, persistPreferences]
  );

  // Toggle compact mode
  const toggleCompact = useCallback(() => {
    setIsCompact((prev) => {
      const next = !prev;
      persistPreferences(favorites, expandedGroups, next);
      return next;
    });
  }, [favorites, expandedGroups, persistPreferences]);

  // Set explicit compact mode
  const setCompactMode = useCallback(
    (val: boolean) => {
      setIsCompact(val);
      persistPreferences(favorites, expandedGroups, val);
    },
    [favorites, expandedGroups, persistPreferences]
  );

  // Reset to default
  const resetPreferences = useCallback(async () => {
    try {
      const companyId = activeCompany?.id || "system";
      const res = await fetch("/api/nav/preferences/reset", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ companyId }),
      });
      const data = await res.json();
      if (data.success && data.preferences) {
        setFavorites(data.preferences.favorites || []);
        setExpandedGroups(data.preferences.expandedGroups || ["OVERVIEW", "ACCOUNTING"]);
        setIsCompact(false);
        setIsCustomized(false);
        toast({ title: "Navigation Reset", description: "Sidebar restored to default layout." });
      }
    } catch (err) {
      console.error("[SmartSidebar] Failed to reset preferences:", err);
    }
  }, [activeCompany?.id, getAuthHeaders, toast]);

  return {
    favorites,
    expandedGroups,
    isCompact,
    isCustomized,
    loading,
    toggleFavorite,
    removeFavorite,
    reorderFavorites,
    toggleGroup,
    expandGroup,
    toggleCompact,
    setCompactMode,
    resetPreferences,
    reloadPreferences: loadPreferences,
  };
}
